/**
 * PWA Standalone Detection & First-Launch Onboarding Flag Helpers
 */

export const ONBOARDING_STORAGE_KEY = 'leton_pwa_onboarding_completed';

/**
 * Checks whether the current session is running in an installed PWA / standalone environment.
 * Supports iOS Safari standalone mode, Android WebAPK, and standard display-mode standalone queries.
 */
export function isPwaStandalone(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    // 1. Standard CSS Media Queries for standalone / fullscreen / minimal-ui
    const isStandaloneDisplay = window.matchMedia('(display-mode: standalone)').matches;
    const isFullscreenDisplay = window.matchMedia('(display-mode: fullscreen)').matches;
    const isMinimalUiDisplay = window.matchMedia('(display-mode: minimal-ui)').matches;

    // 2. iOS Safari Add-to-Home-Screen standalone property
    const isIosStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone === true;

    // 3. Android TWA / WebAPK referrer check
    const isAndroidTwa = typeof document !== 'undefined' && document.referrer.includes('android-app://');

    return Boolean(
      isStandaloneDisplay ||
      isFullscreenDisplay ||
      isMinimalUiDisplay ||
      isIosStandalone ||
      isAndroidTwa
    );
  } catch {
    return false;
  }
}

/**
 * Checks whether the customer has already completed or skipped the first-launch onboarding.
 */
export function isOnboardingCompleted(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    return localStorage.getItem(ONBOARDING_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Sets persistent flag indicating onboarding is completed or skipped.
 */
export function setOnboardingCompleted(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
  } catch (err) {
    console.warn('[PWA] Failed to set onboarding flag:', err);
  }
}
